import React, { useState, useEffect } from "react";
import { useI18n } from "@/context/I18nContext";
import { FORM, DASHBOARD } from "@/constants/testIds";
import { TASK_TYPE_OPTIONS, TASK_TYPE_OTHERS } from "@/constants/taskTypes";

// Decide initial dropdown selection: if the stored task_type matches a
// predefined option, select it; otherwise treat the value as a custom "Otros"
// entry and pre-fill the custom input.
function splitTaskType(value) {
  const v = (value || "").trim();
  if (!v) return { select: "", custom: "" };
  if (TASK_TYPE_OPTIONS.includes(v) && v !== TASK_TYPE_OTHERS) {
    return { select: v, custom: "" };
  }
  return { select: TASK_TYPE_OTHERS, custom: v };
}

export default function TaskForm({ initial, employees = [], orders = [], onSubmit, onCancel, lockedProduct = null, lockedOrderId = null, lockedOrderItemId = null }) {
  const { t } = useI18n();
  const [name, setName] = useState(initial?.name || "");
  const [orderId, setOrderId] = useState(initial?.order_id || lockedOrderId || "");
  const [employeeId, setEmployeeId] = useState(initial?.employee_id || "");
  const [due, setDue] = useState(initial?.due_date?.slice(0, 10) || "");
  const [product, setProduct] = useState(lockedProduct ?? initial?.product ?? "");
  const [status, setStatus] = useState(initial?.status || "pending");
  const [priority, setPriority] = useState(initial?.priority || "normal");
  const initialType = splitTaskType(initial?.task_type);
  const [taskTypeSelect, setTaskTypeSelect] = useState(initialType.select);
  const [taskTypeCustom, setTaskTypeCustom] = useState(initialType.custom);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(initial?.name || "");
    setOrderId(initial?.order_id || lockedOrderId || "");
    setEmployeeId(initial?.employee_id || "");
    setDue(initial?.due_date?.slice(0, 10) || "");
    setProduct(lockedProduct ?? initial?.product ?? "");
    setStatus(initial?.status || "pending");
    setPriority(initial?.priority || "normal");
    const tt = splitTaskType(initial?.task_type);
    setTaskTypeSelect(tt.select);
    setTaskTypeCustom(tt.custom);
    // Feb 2026 · bugfix — depender de `initial?.id` (no del objeto) para
    // que un re-render del padre con `initial={{}}` (referencia nueva) no
    // resetee los campos que el usuario está escribiendo.
  }, [initial?.id, lockedProduct, lockedOrderId]);

  const isOthers = taskTypeSelect === TASK_TYPE_OTHERS;
  const resolvedTaskType = isOthers ? taskTypeCustom.trim() : taskTypeSelect;
  const isProductLocked = lockedProduct != null;
  const isOrderLocked = lockedOrderId != null;

  // GP 1.0 · Sprint 5 · Issue #8 — Auto-fill task_name from task_type so the
  // operator does not have to type it twice. Backend still receives `name`
  // for full compatibility with existing tasks and reports.
  useEffect(() => {
    if (resolvedTaskType) setName(resolvedTaskType);
  }, [resolvedTaskType]);

  const handle = async (e) => {
    e.preventDefault();
    if (!resolvedTaskType) return; // required guard (HTML required also enforces)
    setLoading(true);
    try {
      await onSubmit({
        name: resolvedTaskType || name,
        order_id: orderId || null,
        order_item_id: lockedOrderItemId || initial?.order_item_id || null,
        employee_id: employeeId || null,
        due_date: due,
        product,
        status,
        priority,
        task_type: resolvedTaskType,
      });
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full border border-[var(--border)] bg-white px-3 py-2 focus:border-[var(--primary)]";
  return (
    <form onSubmit={handle} className="space-y-4">
      {/* GP 1.0 · Sprint 5 · Issue #8 — task_name is now derived from task_type.
          Kept as a hidden input to preserve DOM contract and testid. */}
      <input
        data-testid={FORM.taskName}
        type="hidden"
        value={name}
        readOnly
      />
      <div>
        <label className="label-overline block mb-1">
          {t("task_type")} <span className="text-[var(--alert-overdue)]">*</span>
        </label>
        <select
          data-testid="task-type-select"
          required
          value={taskTypeSelect}
          onChange={(e) => setTaskTypeSelect(e.target.value)}
          className={input}
        >
          <option value="" disabled>
            {t("task_type_placeholder")}
          </option>
          {TASK_TYPE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {isOthers && (
          <input
            data-testid="task-type-custom"
            required
            value={taskTypeCustom}
            onChange={(e) => setTaskTypeCustom(e.target.value)}
            placeholder={t("task_type_other_placeholder")}
            className={`${input} mt-2`}
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("related_order")}</label>
          <select
            data-testid={FORM.taskOrder}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className={input}
            disabled={isOrderLocked}
          >
            <option value="">{t("none")}</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                #{o.invoice_number} — {o.company_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-overline block mb-1">{t("assigned_to")}</label>
          <select
            data-testid={FORM.taskEmployee}
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className={input}
          >
            <option value="">{t("unassigned")}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name} {emp.role ? `· ${emp.role}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("due_date")}</label>
          <input
            data-testid={FORM.taskDue}
            required
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className={input}
          />
        </div>
        <div>
          <label className="label-overline block mb-1">{t("status")}</label>
          <select
            data-testid={FORM.taskStatus}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={input}
          >
            <option value="pending">{t("task_status_pending")}</option>
            <option value="in_progress">{t("task_status_in_progress")}</option>
            <option value="completed">{t("task_status_completed")}</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label-overline block mb-1">{t("priority")}</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: "normal", label: t("priority_normal"), cls: "bg-white border-[var(--border)] text-[var(--text-primary)]" },
            { value: "priority", label: t("priority_priority"), cls: "bg-amber-300 border-amber-500 text-black" },
            { value: "urgent", label: t("priority_urgent"), cls: "bg-red-600 border-red-700 text-white" },
          ].map((opt) => {
            const active = priority === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                data-testid={`${DASHBOARD.taskPrioritySelect}-${opt.value}`}
                onClick={() => setPriority(opt.value)}
                className={`border-2 px-3 py-2 font-display font-bold tracking-wide text-sm transition-all ${opt.cls} ${active ? "ring-2 ring-offset-1 ring-black" : "opacity-60 hover:opacity-100"}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="label-overline block mb-1">{t("task_description")}</label>
        <input
          data-testid={FORM.taskProduct}
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          className={`${input} ${isProductLocked ? "bg-[var(--surface-alt,#f5f5f5)] cursor-not-allowed" : ""}`}
          placeholder="—"
          readOnly={isProductLocked}
        />
        {isProductLocked && (
          <div
            data-testid="task-product-locked-hint"
            className="mt-1 font-mono-tech text-[11px] text-[var(--text-secondary)]"
          >
            {t("locked_product_hint")}
          </div>
        )}
      </div>
      <div className="flex gap-3 pt-2">
        <button
          data-testid={FORM.taskCancel}
          type="button"
          onClick={onCancel}
          className="flex-1 border border-[var(--border)] px-4 py-2.5 font-display font-medium hover:bg-[var(--surface-alt)]"
        >
          {t("cancel")}
        </button>
        <button
          data-testid={FORM.taskSubmit}
          type="submit"
          disabled={loading}
          className="flex-1 bg-[var(--text-primary)] text-white px-4 py-2.5 font-display font-bold hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {loading ? "..." : t("save")}
        </button>
      </div>
    </form>
  );
}
